// src/components/User/User.jsx
// Main component for User Management page

import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import UserTable from './UserTable';
import UserModal from './UserModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog";
import './user.css';
import '../Inventory/inventory.css';
import { PlusCircle } from 'lucide-react';

/**
 * User Component
 * Main page component that manages users
 * Handles CRUD operations and filtering
 */
const User = ({ toggleSidebar }) => {
  
  // ========== STATE MANAGEMENT ==========
  
  // All users from the backend
  const [users, setUsers] = useState([]);
  
  // Filtered users (after applying search/filters)
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Saving state (for add/edit operations)
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Currently selected user for editing (null for add mode)
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filter values
  const [filters, setFilters] = useState({
    search: '',
    role: ''
  });

  // Delete confirmation modal
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Loading state while deleting
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsConfirmOpen(true);
  };

  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  
  /**
   * Load all users from the backend when component first renders
   * This runs once when the component mounts
   */
  useEffect(() => {
    loadUsers();
  }, []); // Empty dependency array means this runs once on mount

  /**
   * Apply filters whenever users or filter values change
   */
  useEffect(() => {
    applyFilters();
  }, [users, filters]); // Run whenever users or filters change

  // ========== DATA LOADING ==========

  /**
   * Fetch all users from the backend
   */
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      
      // Call the API service
      const data = await userService.getAll();
      
      // Update state with fetched data
      setUsers(data);
      
      // Show success message
      showNotification(`Loaded ${data.length} users`, 'success');
      
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification(`Failed to load users: ${error.message}`, 'error');
      
      // Set empty array on error
      setUsers([]);
      
    } finally {
      // Always stop loading, whether successful or not
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========

  /**
   * Apply current filters to the user list
   */
  const applyFilters = () => {
    const safeUsers = Array.isArray(users) ? users : [];
    let filtered = [...safeUsers];

    // Filter by role if selected
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Filter by search term (searches username and email)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => {
        const username = user.username?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';
        return username.includes(searchLower) || email.includes(searchLower);
      });
    }

    setFilteredUsers(filtered);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      search: '',
      role: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========

  /**
   * Open modal for adding new user
   */
  const handleAddNew = () => {
    setSelectedUser(null); // Clear any selected user
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing existing user
   */
  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  /**
   * Close the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  // ========== CRUD OPERATIONS ==========

  /**
   * Save user (create or update)
   * @param {Object} formData - The form data from the modal
   */
  const handleSave = async (formData) => {
    try {
      setIsSaving(true);

      // Ensure backend-compatible payload
      const payload = {
        username: formData.username,
        email: formData.email,
        role: formData.role?.toUpperCase(),
        employeeId: formData.employeeId ? parseInt(formData.employeeId) : null,
        isActive: formData.isActive,
      };

      // Only include password if it's provided
      if (formData.password && formData.password.trim()) {
        payload.password = formData.password;
      }

      if (selectedUser) {
        await userService.update(selectedUser.id, payload);
        showNotification(
          `User "${payload.username}" updated successfully!`,
          'success'
        );
      } else {
        // Password is required for new users
        if (!formData.password || !formData.password.trim()) {
          throw new Error('Password is required for new users');
        }
        await userService.create(payload);
        showNotification(
          `New user "${payload.username}" added successfully!`,
          'success'
        );
      }

      await loadUsers();
      handleCloseModal();

    } catch (error) {
      console.error('Error saving user:', error);
      showNotification(
        `Failed to save: ${error.message}`,
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Delete a user
   */
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      await userService.delete(userToDelete.id);

      showNotification("User deleted successfully", "success");

      await loadUsers();
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(`Failed to delete: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setUserToDelete(null);
  };

  // ========== RENDER ==========

  return (
    <main className="inventory-main-content">
      
      {/* Page Header */}
      <header className="user-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">
          ☰
        </button>
        <h2 className="user-content-title">User Management</h2>
        <div className="header-actions">
          <button 
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
          ><PlusCircle size={18} />
            Add New User
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="user-filter-controls">
        <div className="user-filter-group">
          
          {/* Search Input */}
          <input 
            type="text"
            name="search"
            className="user-custom-input user-search-input"
            placeholder="Search Username or Email"
            value={filters.search}
            onChange={handleFilterChange}
          />

          {/* Role Filter */}
          <select 
            name="role"
            className="user-custom-select"
            value={filters.role}
            onChange={handleFilterChange}
          >
            <option value="">All Roles</option>
            <option value="SUPERADMIN">SUPERADMIN</option>
            <option value="ADMIN">ADMIN</option>
            <option value="USER">USER</option>
          </select>

          {/* Apply Filter Button */}
          {/* <button 
            className="user-btn user-btn-primary"
            onClick={applyFilters}
          >
            Apply Filter
          </button> */}

          {/* Clear Filters Button */}
          <button 
            className="user-btn user-btn-clear"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <UserTable 
        users={filteredUsers}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />

      {/* Add/Edit Modal */}
      <UserModal 
        key={selectedUser ? selectedUser.id : 'new'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        user={selectedUser}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete User"
        message={`Are you sure you want to delete "${userToDelete?.username}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default User;